import { useState, useEffect, useRef, useCallback } from 'react';
import Services from '../services/Services';
import { ChannelListItem, Channel } from '../types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { useGeneral } from '../contexts';

interface UseChannelsOptions {
  token: string | null;
  userID?: string;
  popular?: boolean;
  all?: boolean;
  start?: number;
  limit?: number;
  loadingKey?: string; // Optional key for centralized loading state
  refreshTrigger?: number; // Optional refresh trigger that forces refetch when changed
}

export const useChannels = (options: UseChannelsOptions) => {
  const { token, userID, popular = false, all = false, start = 0, limit = 10, loadingKey, refreshTrigger } = options;
  const { setLoading, isLoading } = useGeneral();
  const [channels, setChannels] = useState<ChannelListItem[] | Channel[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const fetchControllerRef = useRef<AbortController | null>(null);
  
  // Use centralized loading if key is provided, otherwise use local state
  // Note: centralized loading starts as false; keep localLoading true initially
  // to avoid flashing empty states before the first request starts.
  const loading = loadingKey ? (isLoading(loadingKey) || localLoading) : localLoading;

  const fetchChannels = useCallback(async () => {
    // Abort previous request if any
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
      unregisterAbortController(fetchControllerRef.current);
    }

    const abortController = new AbortController();
    fetchControllerRef.current = abortController;
    registerAbortController(abortController);

    if (!token || isLoggingOut()) {
      if (isMountedRef.current) {
        if (loadingKey) {
          setLoading(loadingKey, false);
        }
        setLocalLoading(false);
      }
      return;
    }

    try {
      if (isMountedRef.current) {
        if (loadingKey) {
          setLoading(loadingKey, true);
        }
        setLocalLoading(true);
        setError(null);
      }

      let response;
      if (all) {
        response = await Services.SocialHiveGetAllChannels(token, start, limit, abortController);
      } else if (popular) {
        response = await Services.SocialHiveGetPopularChannels(token, abortController);
      } else if (userID) {
        response = await Services.SocialHiveGetMyChannels(token, abortController);
      } else {
        response = await Services.SocialHiveGetMyChannels(token, abortController);
      }

      // Check if request was aborted or component unmounted
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (response.data.status && response.data.result) {
        setChannels(response.data.result);
      } else {
        setError(response.data.message || 'Failed to fetch channels');
      }
    } catch (err: any) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message?.includes('Logout in progress')) {
        return;
      }

      if (isMountedRef.current) {
        setError(err.message || 'An error occurred');
        setChannels([]);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        if (loadingKey) {
          setLoading(loadingKey, false);
        }
        setLocalLoading(false);
      } else if (loadingKey) {
        // Clear loading even if unmounted to prevent stuck state
        setLoading(loadingKey, false);
      }
      if (fetchControllerRef.current === abortController) {
        fetchControllerRef.current = null;
      }
    }
  }, [token, userID, popular, all, start, limit, loadingKey, setLoading]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clean up any pending requests
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
        unregisterAbortController(fetchControllerRef.current);
        fetchControllerRef.current = null;
      }
      // Clear loading state on cleanup to prevent stuck state
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  }, [loadingKey, setLoading]);

  useEffect(() => {
    fetchChannels();

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
        unregisterAbortController(fetchControllerRef.current);
        fetchControllerRef.current = null;
      }
      // Clear loading state on cleanup to prevent stuck state
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  }, [fetchChannels, refreshTrigger, loadingKey, setLoading]);

  const refresh = useCallback(() => {
    fetchChannels();
  }, [fetchChannels]);

  return { channels, loading, error, refresh };
};

